/*
 *   File : add-notification.js
 *   Author : https://evoqins.com
 *   Description : add notification component
 *   Integrations : NA
 *   Version : 1.0.0
 */
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  setNavLink,
  setPageTitle,
  setSearchEnabled,
} from "../../../../Redux/Ui/ui.slice";
import {
  CheckboxSelect,
  CustomFileUpload,
  CustomTextArea,
  Input,
} from "../../../../Components/FormElements";
import { PrimaryButton } from "../../../../Components/Buttons";
import { useNavigate } from "react-router-dom";
import { ConfirmNotificationModal } from "../../../../Components/Modals";
import { APIService } from "../../../../Service";
import { toast } from "react-toastify";

export default function AddNotification() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  /* ----------------------------------------
     STATES
  ---------------------------------------- */
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [title, setTitle] = useState("");
  const [titleError, setTitleError] = useState("");

  const [description, setDescription] = useState("");
  const [descriptionError, setDescriptionError] = useState("");

  const [logoFile, setLogoFile] = useState(null);
  const [logoUrl, setLogoUrl] = useState("");
  const [logoError, setLogoError] = useState("");

  const [targetUsers, setTargetUsers] = useState([]);
  const [type, setType] = useState([]);

  const [filterData, setFilterData] = useState([]);

  const [errors, setErrors] = useState({
    targetUsers: "",
    type: "",
  });

  const [loading, setLoading] = useState(false);

  /* ----------------------------------------
     PAGE SETUP
  ---------------------------------------- */
  useEffect(() => {
    _getFilterData();
    dispatch(setPageTitle(["Manage Content", "Add Notification"]));
    dispatch(setSearchEnabled(false));
    dispatch(setNavLink(["/manage-content/notification"]));
  }, []);

  useEffect(() => {
    if (description?.length >= 99) {
      setDescriptionError("words should be less 100");
    }
  }, [description]);
  /* ----------------------------------------
     VALIDATION
  ---------------------------------------- */
  const validate = () => {
    let valid = true;

    if (!title.trim()) {
      setTitleError("Title is required");
      valid = false;
    }

    if (!description.trim()) {
      setDescriptionError("Description is required");
      valid = false;
    } else if (description.length > 100) {
      setDescriptionError("Maximum 100 characters allowed");
      valid = false;
    }

    if (!logoFile && !logoUrl) {
      setLogoError("Logo image is required");
      valid = false;
    }

    if (targetUsers.length === 0) {
      setErrors((e) => ({
        ...e,
        targetUsers: "Please select at least one user",
      }));
      valid = false;
    }

    if (type.length === 0) {
      setErrors((e) => ({ ...e, type: "Please select notification type" }));
      valid = false;
    }

    return valid;
  };

  /* ----------------------------------------
     FORM SUBMIT
  ---------------------------------------- */
  const _submitForm = () => {
    if (!validate()) return;

    setLoading(true);

    const payload = {
      title: title?.trim(),
      description: description?.trim(),
      image: logoUrl,
      type: type?.value,
      target_users: targetUsers?.value,
    };
    APIService(true, "content/create", payload)
      .then((res) => {
        if (res?.status_code === 200) {
          toast.success(res?.data?.message || res?.message);
          navigate("/manage-content/notification");
        } else {
          toast.error(res?.message || "Something went wrong");
        }
      })
      .catch((error) => {
        console.error("Error submitting notification:", error);
        toast.error(error?.message || "Something went wrong");
      })
      .finally(() => {
        setLoading(false);
        setIsModalOpen(false);
      });
  };

  const _getFilterData = async () => {
    const response = await APIService(
      false,
      "content/notification-filter-data",
    );
    if (response?.status_code !== 200) {
      toast.error(response?.message);
      return;
    }
    setFilterData(response.data);
  };

  const handleCoverUploadComplete = (uploadedUrl) => {
    setLogoUrl(uploadedUrl);
    setLogoError("");
  };

  /* ----------------------------------------
     RENDER
  ---------------------------------------- */
  return (
    <div className="row">
      <div className="col-12">
        <div className="d-flex flex-column e-glass-card rounded-24 p-24">
          <div className="row">
            <div className="col-6">
              {/* TITLE */}
              <Input
                id="title"
                type="text"
                label="Title"
                placeholder="Add a title"
                require={true}
                value={title}
                error={titleError}
                className="w-100 px-2 py-10"
                handleChange={(val) => {
                  setTitle(val);
                  setTitleError("");
                }}
                autoFocus={true}
              />

              {/* DESCRIPTION */}
              <div className="d-flex flex-column">
                <p className="fw-400 fs-14 mb-0">
                  Content
                  <span className="text-davys-gray fw-400 fs-12">
                    {" "}
                    (max characters up to 100)
                  </span>
                  <span className="text-cg-red">*</span>
                </p>

                <CustomTextArea
                  placeholder="Enter description"
                  value={description}
                  handleChange={(val) => {
                    if (val?.length > 99) return;
                    setDescription(val);
                    setDescriptionError("");
                  }}
                  error={descriptionError}
                  rows={6}
                  required
                  className="w-100 px-2 py-10"
                />
              </div>

              {/* LOGO UPLOAD */}
              <div className="d-flex flex-column row-gap-2 pt-2">
                <p className="mb-0 fw-400 fs-14">
                  Upload Image <span className="text-cg-red">*</span>
                </p>

                <CustomFileUpload
                  file={logoFile || logoUrl}
                  type={2}
                  fileSize={3}
                  error={logoError}
                  handleClear={() => {
                    setLogoFile(null);
                    setLogoUrl("");
                  }}
                  handleChange={(file) => {
                    setLogoFile(file);
                    setLogoError("");
                  }}
                  onUploadComplete={handleCoverUploadComplete}
                />
              </div>

              {/* TARGET USERS */}
              <div className="d-flex flex-column pt-3">
                <p className="mb-0 fw-400 fs-14 pb-1">
                  Target Users <span className="text-cg-red">*</span>
                </p>

                <CheckboxSelect
                  options={filterData?.target_users}
                  value={targetUsers}
                  onChange={(val) => {
                    setTargetUsers(val);
                    setErrors((e) => ({ ...e, targetUsers: "" }));
                  }}
                  placeholder="Select Users"
                  type={1}
                  error={errors.targetUsers}
                />
              </div>

              {/* NOTIFICATION TYPE */}
              <div className="d-flex flex-column pt-3">
                <p className="mb-0 fw-400 fs-14 pb-1">
                  Type <span className="text-cg-red">*</span>
                </p>

                <CheckboxSelect
                  options={filterData?.notification_types}
                  value={type}
                  onChange={(val) => {
                    setType(val);
                    setErrors((e) => ({ ...e, type: "" }));
                  }}
                  placeholder="Select Notification Type"
                  type={1}
                  error={errors.type}
                />
              </div>

              {/* BUTTONS */}
              <div className="row row-cols-2 pt-24">
                <div className="col">
                  <PrimaryButton
                    name="Cancel"
                    ClassName="rounded-pill bg-transparent border-oxford-blue py-3 text-oxford-blue w-100 fs-16 fw-500"
                    onPress={() => navigate("/manage-content/notification")}
                  />
                </div>

                <div className="col">
                  <PrimaryButton
                    name={loading ? "Adding..." : "Add"}
                    ClassName="rounded-pill bg-oxford-blue py-3 text-white w-100 fs-16 fw-500"
                    onPress={
                      loading
                        ? () => {}
                        : () => {
                            if (!validate()) return;
                            setIsModalOpen(true);
                          }
                    }
                    disabled={loading}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <ConfirmNotificationModal
        open={isModalOpen}
        title={title}
        content={description}
        time={"3m ago"}
        loading={loading}
        onCancel={() => setIsModalOpen(false)}
        onPress={_submitForm}
      />
    </div>
  );
}
